// posthog-service.ts
import posthog from "posthog-js";
import { TrackerPageViewData, TrackerService, TrackerServiceProps } from "./tracker-service";
import { takeId } from "shared";
import { ENVIRONMENT, POSTHOG_HOST, POSTHOG_KEY } from "../../config";

/**
 * Call once on app bootstrap.
 * Example config:
 *   export const POSTHOG_KEY = 'phc_...';
 *   export const POSTHOG_HOST = 'https://eu.i.posthog.com';
 */
export function initPostHog() {
	if (typeof window === "undefined") return;
	if (!POSTHOG_KEY) return;

	posthog.init(POSTHOG_KEY, {
		api_host: POSTHOG_HOST || "https://us.i.posthog.com",
		person_profiles: "identified_only",
		capture_pageview: false, // we manage pageviews manually to attach props
		session_recording: {
			maskAllInputs: true,
			maskTextSelector: "*"
		}
	});

	posthog.register({ environment: ENVIRONMENT });
}

export class PostHogService implements TrackerService {
	setup(props: TrackerServiceProps): Promise<void> | void {
		initPostHog();
		this.ensureIdentity(props);
	}

	private getPropsData(props?: TrackerServiceProps) {
		const { user, workspace, room } = props || {};
		return {
			workspace: workspace != null ? takeId(workspace) : undefined,
			room: room != null ? takeId(room) : undefined,
			user: user != null ? takeId(user) : undefined
		};
	}

	private ensureIdentity(props?: TrackerServiceProps) {
		if (typeof window === "undefined") return;
		const ids = this.getPropsData(props);

		if (ids.user) {
			posthog.identify(ids.user);
		}

		if (ids.workspace) {
			posthog.group("workspace", ids.workspace);
		}

		const platform = props?.platform || "webapp";
		posthog.register({
			platform,
			...ids
		});
	}

	async trackPageView({ data, props }: { data?: TrackerPageViewData; props?: TrackerServiceProps }) {
		return this.track({ name: "$pageview", baseData: data, props });
	}

	async trackEvent(
		name: string,
		{ data, props, pageData }: { data?: object; props?: TrackerServiceProps; pageData?: TrackerPageViewData }
	) {
		const baseData = pageData;
		return this.track({ name, baseData, eventData: data, props });
	}

	private async track({
		name,
		baseData,
		eventData,
		props
	}: { baseData?: object; name?: string; eventData?: any; props?: TrackerServiceProps } = {}) {
		if (typeof window === "undefined") return;

		this.ensureIdentity(props);

		const platform = props?.platform || "webapp";
		const ids = this.getPropsData(props);

		const eventName = name ?? "$pageview";
		const properties = {
			...(baseData ?? {}),
			...(eventData ?? {}),
			platform,
			...ids
		};

		posthog.capture(eventName, properties);
	}
}

export const posthogService = new PostHogService();
